import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Sparkles, Star, Clock } from "lucide-react";
import {
    computeInsights,
    computePersonalInsights,
    computeMirrorReflection,
    computeBeforeAfterImpact,
    computeHeatmapObservations,
    getCachedMirror,
    setCachedMirror,
    STYLE_LABELS,
} from "@/lib/insights";
import { readLocalCounters } from "@/lib/analytics";
import { loadStore, flattenAllMessages } from "@/lib/chats";
import BeforeAfterImpactCard from "@/components/BeforeAfterImpactCard";
import {
    detectRewriteBehaviour,
    detectEmotionalTrend,
    detectCommunicationDrift,
    flattenRegisterObservations,
} from "@/lib/toneMirrorLayers";

const INSIGHT_HASHES_KEY = "toneguard:shownInsightHashes";
const MAX_HASH_HISTORY = 6;

// Aggregate across every chat in the v2 store, so insights reflect the
// user's overall communication — not just the active chat.
const loadPersisted = () => {
    if (typeof window === "undefined") {
        return {
            messages: [],
            preferredStyle: null,
            defaultStyle: null,
            store: null,
        };
    }
    try {
        const store = loadStore();
        const messages = flattenAllMessages(store);
        // Pick the preferred style of the most-recently-active chat that
        // has one set, otherwise null.
        let preferredStyle = null;
        const sorted = [...(store.chats || [])].sort(
            (a, b) =>
                new Date(b.lastActivityAt || 0).getTime() -
                new Date(a.lastActivityAt || 0).getTime(),
        );
        for (const c of sorted) {
            if (c.preferredStyle) {
                preferredStyle = c.preferredStyle;
                break;
            }
        }
        // ToneMirror Layers 2+3 need the user-facing communication style
        // key (calm | direct | warm | professional). Stored device-wide.
        const defaultStyle = store.defaultStyle || null;
        return { messages, preferredStyle, defaultStyle, store };
    } catch {
        return {
            messages: [],
            preferredStyle: null,
            defaultStyle: null,
            store: null,
        };
    }
};

// Risk bucket meta — earthy palette, kept consistent with the chip colors.
// Risky → coral-red (warmer, more saturated); Needs Softening → honey-amber
// (clearly yellow-leaning); Good → sage. Hue separation ≥ 30° so the three
// states are immediately distinguishable at a glance — earlier palette had
// Risky and Needs Softening sharing the same orange family.
const RISK_CARDS = [
    {
        key: "risky",
        label: "Risky",
        accent: "text-[#8C3A2F]",
        dot: "bg-[#B5483A]",
        ring: "border-[#F1C5BC]",
        wash: "bg-[#FBE2DC]/70",
    },
    {
        key: "needs_softening",
        label: "Needs Softening",
        accent: "text-[#8C6420]",
        dot: "bg-[#D89A2F]",
        ring: "border-[#EFD9A3]",
        wash: "bg-[#FBF1DC]/70",
    },
    {
        key: "good",
        label: "Good",
        accent: "text-[#4B6143]",
        dot: "bg-[#5E7153]",
        ring: "border-[#D5E3D1]",
        wash: "bg-[#EAF1E8]/55",
    },
];

// Risk → palette (matches the chip colors).
const RISK_PALETTE_BY_KEY = {
    risky: { dot: "bg-[#B5483A]", text: "text-[#8C3A2F]" },
    needs_softening: { dot: "bg-[#D89A2F]", text: "text-[#8C6420]" },
    good: { dot: "bg-[#5E7153]", text: "text-[#4B6143]" },
    neutral: { dot: "bg-stone-400", text: "text-stone-600" },
};

// Human-readable eyebrow for each coach insight type.
const TYPE_LABELS = {
    pattern: "Your style",
    imbalance: "Try a different tone",
    refinement: "Your careful intent",
    copy_gap: "Worth fine-tuning",
    trend: "This week",
    risk: "Worth a look",
    contrast: "How you handle boundaries",
};

const InsightsPage = () => {
    const [{ messages, preferredStyle, defaultStyle, store }, setState] = useState(
        () => loadPersisted(),
    );

    // Refresh if user navigates back into the tab (no API needed).
    useEffect(() => {
        const onFocus = () => setState(loadPersisted());
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, []);

    const insights = useMemo(() => computeInsights(messages), [messages]);

    // Personalized coach insights (1 primary + optional secondary).
    // De-duped against the last few hashes shown so repeat visits feel fresh.
    const prevHashes = useMemo(() => {
        try {
            const raw = window.localStorage.getItem(INSIGHT_HASHES_KEY);
            if (!raw) return [];
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed.slice(0, MAX_HASH_HISTORY) : [];
        } catch {
            return [];
        }
    }, []);
    const personalInsights = useMemo(
        () => computePersonalInsights(messages, prevHashes, readLocalCounters()),
        [messages, prevHashes],
    );

    // Mirror scope tab — Weekly / Monthly / Yearly. Cached locally to keep
    // repeat visits instant + credit-free.
    const [mirrorScope, setMirrorScope] = useState("weekly");
    const mirror = useMemo(() => {
        const cached = getCachedMirror(mirrorScope);
        if (cached) return cached;
        const fresh = computeMirrorReflection(messages, mirrorScope);
        if (fresh) setCachedMirror(mirrorScope, fresh);
        return fresh;
    }, [messages, mirrorScope]);

    // Before vs After Impact aggregates — pure heuristics, instant.
    const impact = useMemo(
        () => computeBeforeAfterImpact(messages),
        [messages],
    );

    // Heatmap observations (time-of-day / day-of-week patterns).
    const heatmap = useMemo(
        () => computeHeatmapObservations(messages),
        [messages],
    );

    // ToneMirror Layers 2 + 3 — additive engines. Both fire only when the
    // sessionLog meets their thresholds AND the user has a communication
    // style set. Graceful degrade: when no chats carry a register, both
    // layers still produce observations under a synthetic "all chats"
    // bucket; only the register-specific templates are skipped.
    const layer2 = useMemo(
        () =>
            detectRewriteBehaviour({
                store,
                currentStyle: defaultStyle,
            }),
        [store, defaultStyle],
    );
    const layer3 = useMemo(
        () => detectEmotionalTrend({ currentStyle: defaultStyle }),
        [defaultStyle],
    );
    // Layer 4 runs AFTER Layer 3 — passes the Layer 3 state in so the
    // ordering is explicit. Has its own drift-fatigue state in
    // localStorage; this useMemo intentionally only re-runs when style
    // or session base changes (the engine reads localStorage directly).
    const layer4 = useMemo(
        () =>
            detectCommunicationDrift({
                currentStyle: defaultStyle,
                layer3State: layer3,
            }),
        [defaultStyle, layer3],
    );
    const layer2Observations = useMemo(
        () => flattenRegisterObservations(layer2.perRegister || {}),
        [layer2],
    );
    const layer3Observations = useMemo(
        () => flattenRegisterObservations(layer3.perRegister || {}),
        [layer3],
    );

    // Persist the newly shown hashes so next visit prefers different insights.
    useEffect(() => {
        if (!personalInsights || personalInsights.length === 0) return;
        try {
            const next = [
                ...personalInsights.map((i) => i.hash).filter(Boolean),
                ...prevHashes,
            ].slice(0, MAX_HASH_HISTORY);
            window.localStorage.setItem(
                INSIGHT_HASHES_KEY,
                JSON.stringify(next),
            );
        } catch {
            /* storage disabled — ignore */
        }
    }, [personalInsights, prevHashes]);

    const hasData = insights.analyzedCount > 0;
    const preferredLabel = preferredStyle
        ? STYLE_LABELS[preferredStyle] || preferredStyle
        : null;

    const primary = personalInsights[0] || null;
    const secondary = personalInsights[1] || null;

    return (
        <div className="tg-grain min-h-screen w-full bg-background flex flex-col">
            {/* Header */}
            <header className="w-full border-b border-border/70 bg-background/80 backdrop-blur-md sticky top-0 z-20">
                <div className="max-w-2xl mx-auto px-5 sm:px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link
                            to="/"
                            data-testid="btn-back-home"
                            className="h-9 w-9 rounded-xl bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
                            aria-label="Back to ToneGuard"
                        >
                            <ArrowLeft className="h-4 w-4" />
                        </Link>
                        <div className="leading-tight">
                            <h1
                                data-testid="insights-title"
                                className="font-serif text-[22px] text-foreground"
                            >
                                Your communication coach
                            </h1>
                            <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                                ToneMirror · last {insights.totalMessages} messages
                            </p>
                        </div>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground" data-testid="insights-sync-badge">
                        <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                        synced to your account
                    </div>
                </div>
            </header>

            {/* Body */}
            <main className="flex-1 w-full">
                <div className="max-w-2xl mx-auto px-5 sm:px-6 py-6 sm:py-8 flex flex-col gap-5">
                    {/* 0. Communication Mirror — timeline-aware reflection.
                        Tabs switch between Weekly / Monthly / Yearly scopes.
                        Cached in localStorage with a 4-hour TTL so repeat
                        visits and tab switches stay instant + credit-free.
                        The CARD + TABS stay visible even when the current
                        scope has no data yet — only the reflection content
                        is replaced with a soft placeholder. Without this,
                        a fresh chat would render zero tabs. */}
                    <section
                        data-testid="mirror-card"
                        data-scope={mirrorScope}
                        data-has-reflection={!!mirror}
                        className="tg-enter rounded-2xl border border-border bg-card p-5 sm:p-6"
                    >
                        <div
                            data-testid="mirror-tabs"
                            role="tablist"
                            className="flex items-center gap-1 mb-4 -mt-1"
                        >
                            {[
                                { k: "weekly", label: "This week" },
                                { k: "monthly", label: "This month" },
                                { k: "yearly", label: "This year" },
                            ].map((t) => (
                                <button
                                    key={t.k}
                                    type="button"
                                    role="tab"
                                    aria-selected={mirrorScope === t.k}
                                    data-testid={`mirror-tab-${t.k}`}
                                    onClick={() => setMirrorScope(t.k)}
                                    className={`text-[12px] px-3 py-1.5 rounded-full transition-colors ${
                                        mirrorScope === t.k
                                            ? "bg-[#5E7153]/12 text-[#4B6143] font-semibold"
                                            : "text-muted-foreground hover:text-foreground"
                                    }`}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="h-9 w-9 rounded-full bg-[#5E7153]/15 flex items-center justify-center shrink-0 ring-1 ring-[#5E7153]/25">
                                <Sparkles className="h-4.5 w-4.5 text-[#5E7153]" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5 font-semibold">
                                    Communication Mirror
                                </p>
                                {mirror ? (
                                    <>
                                        <p
                                            data-testid="mirror-primary"
                                            className="font-serif text-[20px] sm:text-[24px] leading-snug text-foreground"
                                        >
                                            {mirror.primary}
                                        </p>
                                        {mirror.secondary && (
                                            <p
                                                data-testid="mirror-secondary"
                                                className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground"
                                            >
                                                {mirror.secondary}
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <p
                                        data-testid="mirror-empty"
                                        className="font-serif text-[18px] sm:text-[20px] leading-snug text-muted-foreground"
                                    >
                                        Not enough activity for{" "}
                                        {mirrorScope === "weekly"
                                            ? "this week"
                                            : mirrorScope === "monthly"
                                              ? "this month"
                                              : "the year"}{" "}
                                        yet — try a wider view, or check a few
                                        messages first.
                                    </p>
                                )}
                            </div>
                        </div>
                    </section>

                    {/* 0.5 Before vs After Emotional Impact — heuristic, no LLM. */}
                    <BeforeAfterImpactCard impact={impact} />

                    {/* 0.75 ToneMirror Layers — additive Rewrite Behaviour
                        (Layer 2) + Emotional Trend (Layer 3) engines.
                        Per-register observations + closed-library register
                        insight + register trend templates. Gracefully
                        degrades to an "all chats" card when no chat has
                        been tagged with a register. The whole section is
                        hidden entirely until at least one layer is active. */}
                    <ToneMirrorLayersSection
                        layer2={layer2}
                        layer3={layer3}
                        layer4={layer4}
                        layer2Observations={layer2Observations}
                        layer3Observations={layer3Observations}
                        currentStyle={defaultStyle}
                    />

                    {/* 1. Personalized coach insights — primary + optional
                        secondary. The whole section disappears when no
                        rule-driven signal is strong enough to say something
                        specific; we never show generic "you're doing well". */}
                    {personalInsights.length > 0 && (
                        <section
                            data-testid="coach-insights"
                            className="tg-enter flex flex-col gap-3"
                        >
                            {primary && (
                                <div
                                    data-testid="insight-primary"
                                    data-insight-type={primary.type}
                                    className="rounded-2xl border border-[#D5E3D1] bg-gradient-to-br from-[#EAF1E8]/70 via-card to-card p-5 sm:p-6 shadow-[0_6px_24px_-18px_rgba(94,113,83,0.45)]"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="h-9 w-9 rounded-full bg-[#5E7153]/15 flex items-center justify-center shrink-0 ring-1 ring-[#5E7153]/25">
                                            <Sparkles className="h-4.5 w-4.5 text-[#5E7153]" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-[11px] uppercase tracking-[0.14em] text-[#4B6143]/80 font-semibold mb-1.5">
                                                {TYPE_LABELS[primary.type] || "Coach's note"}
                                            </p>
                                            <p className="font-serif text-[22px] sm:text-[26px] leading-snug text-foreground">
                                                {primary.text}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {secondary && (
                                <div
                                    data-testid="insight-secondary"
                                    data-insight-type={secondary.type}
                                    className="rounded-xl border border-border bg-card/70 px-4 py-3.5 sm:px-5 sm:py-4"
                                >
                                    <div className="flex items-start gap-2.5">
                                        <span
                                            aria-hidden
                                            className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#5E7153] shrink-0"
                                        />
                                        <div className="min-w-0">
                                            <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold mb-0.5">
                                                {TYPE_LABELS[secondary.type] || "Also worth noting"}
                                            </p>
                                            <p className="text-[14px] leading-relaxed text-foreground/85">
                                                {secondary.text}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </section>
                    )}

                    {/* 2. Risk summary — three soft cards */}
                    <section
                        data-testid="risk-summary"
                        className="grid grid-cols-3 gap-2.5 sm:gap-3"
                    >
                        {RISK_CARDS.map((r) => (
                            <div
                                key={r.key}
                                data-testid={`risk-card-${r.key}`}
                                className={`rounded-xl border ${r.ring} ${r.wash} px-3 py-3.5 sm:p-4 flex flex-col gap-2`}
                            >
                                <div className="flex items-center gap-1.5">
                                    <span
                                        className={`h-1.5 w-1.5 rounded-full ${r.dot}`}
                                    />
                                    <p
                                        className={`text-[10px] sm:text-[11px] uppercase tracking-[0.1em] font-semibold ${r.accent}`}
                                    >
                                        {r.label}
                                    </p>
                                </div>
                                <p
                                    className={`font-serif text-3xl sm:text-4xl leading-none ${r.accent}`}
                                >
                                    {insights.riskCounts[r.key]}
                                </p>
                            </div>
                        ))}
                    </section>

                    {/* 2.5 Patterns over time — weekly / monthly / yearly +
                        time-of-day heatmap observations. */}
                    {(insights.timeInsights?.weekly ||
                        insights.timeInsights?.monthly ||
                        insights.timeInsights?.yearly ||
                        heatmap?.length > 0) && (
                        <section
                            data-testid="time-insights"
                            className="rounded-2xl border border-border bg-card p-5 sm:p-6 flex flex-col gap-4"
                        >
                            <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                                Patterns over time
                            </p>
                            {insights.timeInsights.weekly && (
                                <TimeInsightRow
                                    testid="time-insight-weekly"
                                    label="This week"
                                    lines={insights.timeInsights.weekly}
                                />
                            )}
                            {insights.timeInsights.monthly && (
                                <TimeInsightRow
                                    testid="time-insight-monthly"
                                    label="This month"
                                    lines={insights.timeInsights.monthly}
                                />
                            )}
                            {insights.timeInsights.yearly && (
                                <TimeInsightRow
                                    testid="time-insight-yearly"
                                    label="Over time"
                                    lines={insights.timeInsights.yearly}
                                />
                            )}
                            {heatmap.length > 0 && (
                                <div
                                    data-testid="heatmap-observations"
                                    className="pt-1 border-t border-border/60 -mt-1"
                                >
                                    <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mt-4 mb-2 font-semibold">
                                        Rhythm observations
                                    </p>
                                    <ul className="flex flex-col gap-1.5">
                                        {heatmap.map((obs) => (
                                            <li
                                                key={obs.key}
                                                data-testid={`heatmap-${obs.key}`}
                                                className="flex items-start gap-2 text-[13px] leading-relaxed text-foreground/85"
                                            >
                                                <Clock className="h-3.5 w-3.5 text-muted-foreground mt-1 shrink-0" />
                                                {obs.text}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </section>
                    )}

                    {/* 3. Original messages — every tone, full breakdown */}
                    <section
                        data-testid="signal-breakdown"
                        className="rounded-2xl border border-border bg-card p-5 sm:p-6"
                    >
                        <div className="flex items-baseline justify-between gap-3 mb-3">
                            <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                                Original messages by tone
                            </p>
                            <p className="text-[11px] text-muted-foreground">
                                {insights.analyzedCount} total
                            </p>
                        </div>
                        {insights.signalBreakdown.length > 0 ? (
                            <ul className="flex flex-col divide-y divide-border/60">
                                {insights.signalBreakdown.map((s) => {
                                    const palette =
                                        RISK_PALETTE_BY_KEY[s.risk] ||
                                        RISK_PALETTE_BY_KEY.neutral;
                                    return (
                                        <li
                                            key={s.signal}
                                            data-testid={`signal-row-${s.signal}`}
                                            className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
                                        >
                                            <span className="flex items-center gap-2.5 min-w-0">
                                                <span
                                                    className={`h-1.5 w-1.5 rounded-full ${palette.dot} shrink-0`}
                                                />
                                                <span
                                                    className={`text-[14px] sm:text-[15px] font-medium ${palette.text} truncate`}
                                                >
                                                    {s.label}
                                                </span>
                                            </span>
                                            <span className="font-serif text-[18px] sm:text-[20px] leading-none text-foreground tabular-nums">
                                                {s.count}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                No tones tracked yet.
                            </p>
                        )}
                        {insights.topSignal?.tip && (
                            <p
                                data-testid="signal-tip"
                                className="text-xs text-muted-foreground leading-relaxed mt-3 pt-3 border-t border-border/60"
                            >
                                <span className="font-semibold text-foreground/80">
                                    Coach:{" "}
                                </span>
                                {insights.topSignal.tip}
                            </p>
                        )}
                    </section>

                    {/* 4. Rewrites — every style + preferred badge */}
                    <section
                        data-testid="style-breakdown"
                        className="rounded-2xl border border-border bg-card p-5 sm:p-6"
                    >
                        <div className="flex items-baseline justify-between gap-3 mb-3">
                            <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                                Rewrites by style
                            </p>
                            <p className="text-[11px] text-muted-foreground">
                                {insights.rewrittenCount} total
                            </p>
                        </div>
                        {insights.styleBreakdown.length > 0 ? (
                            <ul className="flex flex-col divide-y divide-border/60">
                                {insights.styleBreakdown.map((s) => {
                                    const isPreferred =
                                        preferredStyle && s.style === preferredStyle;
                                    return (
                                        <li
                                            key={s.style}
                                            data-testid={`style-row-${s.style}`}
                                            data-preferred={isPreferred || undefined}
                                            className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
                                        >
                                            <span className="flex items-center gap-2 min-w-0">
                                                <span
                                                    className={`text-[14px] sm:text-[15px] font-medium ${
                                                        isPreferred
                                                            ? "text-[#4B6143]"
                                                            : "text-foreground/80"
                                                    } truncate`}
                                                >
                                                    {s.label}
                                                </span>
                                            </span>
                                            <span className="font-serif text-[18px] sm:text-[20px] leading-none text-foreground tabular-nums">
                                                {s.count}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                Pick a rewrite style next time you check tone —
                                your preferences will show up here.
                            </p>
                        )}
                        {preferredLabel && insights.styleBreakdown.length === 0 && (
                            <p
                                data-testid="preferred-style-empty-hint"
                                className="text-xs text-muted-foreground mt-2"
                            >
                                Saved preference:{" "}
                                <span className="text-[#4B6143] font-medium">
                                    {preferredLabel}
                                </span>
                            </p>
                        )}
                    </section>

                    {!hasData && (
                        <div
                            data-testid="insights-empty-cta"
                            className="rounded-xl border border-dashed border-border px-4 py-3 text-center text-sm text-muted-foreground"
                        >
                            <Link
                                to="/"
                                className="text-foreground font-medium underline-offset-2 hover:underline"
                            >
                                Check your first message
                            </Link>{" "}
                            to start building your insights.
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default InsightsPage;

const TimeInsightRow = ({ testid, label, lines }) => (
    <div data-testid={testid} className="flex gap-3">
        <div className="shrink-0 mt-0.5 h-7 w-7 rounded-full bg-[#EAF1E8]/70 border border-[#D5E3D1] flex items-center justify-center">
            <Clock className="h-3.5 w-3.5 text-[#4B6143]" />
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#4B6143]/80 mb-1">
                {label}
            </p>
            {lines.map((l, i) => (
                <p
                    key={i}
                    className="text-[14px] leading-relaxed text-foreground/85"
                >
                    {l}
                </p>
            ))}
        </div>
    </div>
);

// ─────────────────────────────────────────────────────────────────────────
// ToneMirror Layers section (Layer 2 + Layer 3 + Layer 4 — ADDITIVE)
// Renders per-register observations + closed-library register insight
// (Layer 2), register trend (Layer 3), and global drift / aligned summary
// (Layer 4). Layer 4 sits last — background only, never interrupts the
// rewrite flow, only summarises trajectory. Entire section is hidden
// when ALL layers are inactive (cold-start) or produce zero observations.
// ─────────────────────────────────────────────────────────────────────────
const ToneMirrorLayersSection = ({
    layer2,
    layer3,
    layer4,
    layer2Observations,
    layer3Observations,
    currentStyle,
}) => {
    const l2Active = layer2?.active && layer2Observations.some((o) => o.primary);
    const l3Active = layer3?.active && layer3Observations.some((o) => o.primary);
    // Layer 4 is "active" for rendering when it produced a real output
    // (aligned line, drift line, or the one-shot shift prompt). Pure
    // suppression returns no output and should NOT render a card —
    // honoring the global rule "flag=none or threshold not met → show
    // nothing".
    const l4HasContent =
        !!layer4 &&
        layer4.active &&
        (layer4.output || layer4.shiftPrompt);
    if (!l2Active && !l3Active && !l4HasContent) return null;

    return (
        <section
            data-testid="tonemirror-layers"
            data-style={currentStyle || "none"}
            className="tg-enter flex flex-col gap-4"
        >
            <header className="flex items-center justify-between">
                <div>
                    <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
                        ToneMirror · register patterns
                    </p>
                    <p className="text-[12px] text-muted-foreground/80 mt-0.5">
                        Tracks rewrite behaviour and tone trends per relationship context.
                    </p>
                </div>
            </header>

            {l2Active && (
                <LayerCard
                    testid="layer-2-card"
                    eyebrow="Rewrite behaviour"
                    title="How you're choosing rewrites"
                    observations={layer2Observations}
                    registerLine={layer2.registerInsight}
                    registerLineTestid="register-insight"
                />
            )}
            {l3Active && (
                <LayerCard
                    testid="layer-3-card"
                    eyebrow="Emotional trend"
                    title="How your tone is moving"
                    observations={layer3Observations}
                    registerLine={layer3.registerTrend}
                    registerLineTestid="register-trend"
                />
            )}
            {l4HasContent && <Layer4Card layer4={layer4} />}
        </section>
    );
};

const LayerCard = ({
    testid,
    eyebrow,
    title,
    observations,
    registerLine,
    registerLineTestid,
}) => {
    const namedObs = observations.filter((o) => o.register && o.primary);
    const allChatsObs = observations.find(
        (o) => !o.register && o.primary,
    );
    return (
        <div
            data-testid={testid}
            className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-[0_2px_12px_-10px_rgba(40,40,40,0.18)]"
        >
            <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-full bg-[#5E7153]/12 border border-[#5E7153]/22 flex items-center justify-center shrink-0">
                    <Sparkles className="h-4 w-4 text-[#5E7153]" />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-[11px] uppercase tracking-[0.14em] text-[#4B6143]/80 font-semibold mb-1">
                        {eyebrow}
                    </p>
                    <h3 className="font-serif text-[18px] sm:text-[20px] text-foreground leading-snug">
                        {title}
                    </h3>
                </div>
            </div>

            {namedObs.length > 0 ? (
                <ul className="mt-4 flex flex-col gap-3">
                    {namedObs.map((o) => (
                        <li
                            key={o.register}
                            data-testid={`obs-${o.register}`}
                            className="flex flex-col gap-1 rounded-xl border border-border/60 bg-stone-50/60 px-3.5 py-3"
                        >
                            <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">
                                {o.registerPhrase}
                            </p>
                            <p className="text-[14px] leading-relaxed text-foreground">
                                {o.primary}
                            </p>
                            {o.secondary && (
                                <p className="text-[12.5px] text-muted-foreground leading-relaxed">
                                    {o.secondary}
                                </p>
                            )}
                        </li>
                    ))}
                </ul>
            ) : (
                allChatsObs && (
                    <div
                        data-testid="obs-all-chats"
                        className="mt-4 rounded-xl border border-border/60 bg-stone-50/60 px-3.5 py-3"
                    >
                        <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">
                            Across all chats
                        </p>
                        <p className="text-[14px] leading-relaxed text-foreground mt-1">
                            {allChatsObs.primary}
                        </p>
                        {allChatsObs.secondary && (
                            <p className="text-[12.5px] text-muted-foreground leading-relaxed mt-0.5">
                                {allChatsObs.secondary}
                            </p>
                        )}
                        <p className="text-[11px] text-muted-foreground/80 mt-2">
                            Tag a chat with a context (Work · Family · Friend ·
                            Close) to unlock per-relationship insights.
                        </p>
                    </div>
                )
            )}

            {registerLine && (
                <p
                    data-testid={registerLineTestid}
                    className="mt-3 text-[13px] leading-relaxed text-[#4B6143] bg-[#EAF1E8]/40 border border-[#D5E3D1]/60 rounded-xl px-3.5 py-2.5"
                >
                    {registerLine}
                </p>
            )}
        </div>
    );
};



// ─────────────────────────────────────────────────────────────────────────
// Layer 4 card — Communication Drift Engine.
// Background only: a calm, single-line summary of overall trajectory.
// Renders one of:
//   • the aligned / drift_detected output line, OR
//   • the one-shot "Your style preference may have shifted" prompt
//     (surfaced exactly once per style choice after 3 consecutive
//     drift suppressions).
// Drift_detected can additionally carry a register-drift closing line
// when one register clearly drives the shift.
// ─────────────────────────────────────────────────────────────────────────
const Layer4Card = ({ layer4 }) => {
    const isDrift = layer4.flag === "drift_detected";
    const isPrompt = !!layer4.shiftPrompt;
    return (
        <div
            data-testid="layer-4-card"
            data-flag={layer4.flag}
            data-prompt={isPrompt}
            className={[
                "rounded-2xl border p-5 sm:p-6 shadow-[0_2px_12px_-10px_rgba(40,40,40,0.18)]",
                isDrift || isPrompt
                    ? "border-[#EFD9A3] bg-[#FBF1DC]/40"
                    : "border-[#D5E3D1] bg-[#EAF1E8]/30",
            ].join(" ")}
        >
            <div className="flex items-start gap-3">
                <div
                    className={[
                        "h-9 w-9 rounded-full border flex items-center justify-center shrink-0",
                        isDrift || isPrompt
                            ? "bg-[#D89A2F]/12 border-[#D89A2F]/22"
                            : "bg-[#5E7153]/12 border-[#5E7153]/22",
                    ].join(" ")}
                >
                    <Star
                        className={[
                            "h-4 w-4",
                            isDrift || isPrompt
                                ? "text-[#8C6420]"
                                : "text-[#4B6143]",
                        ].join(" ")}
                    />
                </div>
                <div className="min-w-0 flex-1">
                    <p
                        className={[
                            "text-[11px] uppercase tracking-[0.14em] font-semibold mb-1",
                            isDrift || isPrompt
                                ? "text-[#8C6420]"
                                : "text-[#4B6143]",
                        ].join(" ")}
                    >
                        Communication drift
                    </p>
                    <h3 className="font-serif text-[18px] sm:text-[20px] text-foreground leading-snug">
                        {isPrompt ? "Worth a check-in" : "Trajectory overview"}
                    </h3>
                </div>
            </div>

            {isPrompt ? (
                <p
                    data-testid="layer-4-shift-prompt"
                    className="mt-4 text-[15px] leading-relaxed text-foreground"
                >
                    {layer4.shiftPrompt}
                </p>
            ) : (
                layer4.output && (
                    <p
                        data-testid="layer-4-output"
                        className="mt-4 text-[14.5px] leading-relaxed text-foreground"
                    >
                        {layer4.output}
                    </p>
                )
            )}

            {layer4.registerDrift && (
                <p
                    data-testid="layer-4-register-drift"
                    className="mt-3 text-[13px] leading-relaxed text-[#8C6420] bg-[#FBF1DC]/60 border border-[#EFD9A3]/70 rounded-xl px-3.5 py-2.5"
                >
                    {layer4.registerDrift}
                </p>
            )}
        </div>
    );
};
